// Resource and naval building prefabs: the five mines, harbour, shipyard,
// grand forge and grand shipyard. Adapted from adventure-craft.
import { box, hollowBox } from './buildings_shared.js';
import type { StructurePrefab } from './types.js';

// Mine prefabs — stone entrance with colored accents.
export const ironMine: StructurePrefab = {
  name: 'Iron Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#808080', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#787878', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#686868', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#585858', 'stone'),
    { x: 0, y: 6, z: 0, color: '#cc6644', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#cc6644', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#cc6644', type: 'stone' },
  ],
};

export const silverMine: StructurePrefab = {
  name: 'Silver Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#909090', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#888888', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#787878', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#686868', 'stone'),
    { x: 0, y: 6, z: 0, color: '#c0c0e0', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#c0c0e0', type: 'stone' },
  ],
};

export const goldMine: StructurePrefab = {
  name: 'Gold Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#a08850', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#908040', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#807030', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#706020', 'stone'),
    { x: 0, y: 6, z: 0, color: '#ffd700', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 2, z: -1, color: '#ffd700', type: 'stone' },
  ],
};

export const crystalMine: StructurePrefab = {
  name: 'Crystal Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#706080', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#807090', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#706080', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#605070', 'stone'),
    { x: 0, y: 6, z: 0, color: '#cc88ff', type: 'stone' },
    { x: -2, y: 1, z: 2, color: '#cc88ff', type: 'stone' },
    { x: 2, y: 2, z: -2, color: '#aa66ee', type: 'stone' },
  ],
};

export const darkCrystalMine: StructurePrefab = {
  name: 'Dark Crystal Mine',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#302030', 'stone'),
    ...hollowBox(-2, 1, -2, 5, 3, 5, '#403040', 'stone'),
    ...box(-2, 4, -2, 5, 1, 5, '#382838', 'stone'),
    ...box(-1, 5, -1, 3, 1, 3, '#302030', 'stone'),
    { x: 0, y: 6, z: 0, color: '#6600cc', type: 'stone' },
    { x: -2, y: 1, z: -2, color: '#6600cc', type: 'stone' },
    { x: 2, y: 2, z: 2, color: '#440088', type: 'stone' },
  ],
};

// Harbour — wooden pier extending into water.
export const harbour: StructurePrefab = {
  name: 'Harbour',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#8b7355', 'plank'),
    ...box(-2, 1, -2, 5, 1, 5, '#a08060', 'plank'),
    ...box(-4, 0, -1, 2, 3, 3, '#6b4c32', 'wood'),
    ...box(3, 0, -1, 2, 3, 3, '#6b4c32', 'wood'),
    ...box(-3, 0, -5, 7, 1, 3, '#8b7355', 'plank'),
    ...box(-2, 0, -5, 3, 1, 1, '#a08060', 'plank'),
    { x: 0, y: 1, z: -4, color: '#c4a45a', type: 'wood' },
    { x: -1, y: 2, z: -4, color: '#ff4444', type: 'wood' },
    { x: 1, y: 2, z: -4, color: '#ff4444', type: 'wood' },
  ],
};

// Shipyard — larger harbour with construction frame.
export const shipyard: StructurePrefab = {
  name: 'Shipyard',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#8b7355', 'plank'),
    ...box(-3, 1, -3, 7, 1, 7, '#a08060', 'plank'),
    ...box(-5, 0, -2, 2, 4, 5, '#6b4c32', 'wood'),
    ...box(4, 0, -2, 2, 4, 5, '#6b4c32', 'wood'),
    ...box(-4, 0, -6, 9, 1, 3, '#8b7355', 'plank'),
    ...box(-3, 0, -6, 7, 1, 1, '#a08060', 'plank'),
    ...box(-2, 4, -2, 5, 1, 5, '#6b4c32', 'wood'),
    { x: 0, y: 1, z: -5, color: '#c4a45a', type: 'wood' },
    { x: -1, y: 2, z: -5, color: '#ff4444', type: 'wood' },
    { x: 1, y: 2, z: -5, color: '#ff4444', type: 'wood' },
    { x: 2, y: 2, z: -5, color: '#44ff44', type: 'wood' },
  ],
};

// Grand Forge — massive industrial forge with chimney stacks.
export const grandForge: StructurePrefab = {
  name: 'Grand Forge',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#484848', 'stone'),
    ...hollowBox(-5, 1, -5, 11, 5, 11, '#585858', 'stone'),
    // Chimney stacks
    ...box(-4, 6, -4, 2, 4, 2, '#404040', 'stone'),
    ...box(3, 6, -4, 2, 4, 2, '#404040', 'stone'),
    ...box(-4, 6, 3, 2, 4, 2, '#404040', 'stone'),
    ...box(3, 6, 3, 2, 4, 2, '#404040', 'stone'),
    // Forge glow
    { x: 0, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: 1, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: -1, y: 1, z: 0, color: '#ff6600', type: 'stone' },
    { x: 0, y: 2, z: 0, color: '#ff8800', type: 'stone' },
    { x: 1, y: 1, z: 1, color: '#ff6600', type: 'stone' },
    { x: -1, y: 1, z: -1, color: '#ff6600', type: 'stone' },
    // Chimney fire tops
    { x: -3, y: 10, z: -3, color: '#ff4400', type: 'stone' },
    { x: 4, y: 10, z: -3, color: '#ff4400', type: 'stone' },
    { x: -3, y: 10, z: 4, color: '#ff4400', type: 'stone' },
    { x: 4, y: 10, z: 4, color: '#ff4400', type: 'stone' },
    // Anvil block
    { x: 0, y: 1, z: -3, color: '#303030', type: 'stone' },
    { x: 1, y: 1, z: -3, color: '#303030', type: 'stone' },
    { x: 0, y: 2, z: -3, color: '#404040', type: 'stone' },
  ],
};

// Grand Shipyard — capstone naval building. Larger than shipyard with twin dry docks.
export const grandShipyard: StructurePrefab = {
  name: 'Grand Shipyard',
  blocks: [
    ...box(-5, 0, -5, 11, 1, 11, '#8b7355', 'plank'),
    ...box(-4, 1, -4, 9, 1, 9, '#a08060', 'plank'),
    // Twin dry dock frames
    ...box(-6, 0, -3, 2, 5, 7, '#5a3c28', 'wood'),
    ...box(5, 0, -3, 2, 5, 7, '#5a3c28', 'wood'),
    // Cross beams
    ...box(-5, 5, -2, 11, 1, 5, '#6b4c32', 'wood'),
    // Extended pier into water
    ...box(-5, 0, -8, 11, 1, 4, '#8b7355', 'plank'),
    ...box(-3, 0, -9, 7, 1, 2, '#a08060', 'plank'),
    // Crane posts
    { x: -4, y: 1, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 2, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 3, z: -5, color: '#4a3020', type: 'wood' },
    { x: -4, y: 4, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 1, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 2, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 3, z: -5, color: '#4a3020', type: 'wood' },
    { x: 4, y: 4, z: -5, color: '#4a3020', type: 'wood' },
    // Crane arms
    { x: -4, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: -3, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: -2, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 4, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 3, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    { x: 2, y: 5, z: -5, color: '#6b4c32', type: 'wood' },
    // Gold markers at pier end
    { x: 0, y: 1, z: -8, color: '#ffd700', type: 'wood' },
    { x: -1, y: 2, z: -8, color: '#ffd700', type: 'wood' },
    { x: 1, y: 2, z: -8, color: '#ffd700', type: 'wood' },
    // Red + green channel markers
    { x: -2, y: 1, z: -7, color: '#ff4444', type: 'wood' },
    { x: 2, y: 1, z: -7, color: '#44ff44', type: 'wood' },
  ],
};
